import { Link } from "react-router-dom";
import { Clock, Route, ArrowRight } from "lucide-react";
import { useLang, waLink } from "@/i18n";
import { TOURS } from "@/data";
import { Reveal, Kicker } from "@/components/Reveal";

const Tours = ({ onEnquire }) => {
  const { lang, t } = useLang();
  return (
    <section id="tours" className="relative py-24 sm:py-32 bg-ink/40 scroll-mt-20" data-testid="tours-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <Reveal className="max-w-3xl">
          <Kicker>{t.tours.kicker}</Kicker>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-50">
            {t.tours.title}
          </h2>
          <p className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed">{t.tours.sub}</p>
        </Reveal>

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {TOURS.map((tour, i) => (
            <Reveal key={tour.id} delay={(i % 3) * 0.1}>
              <article
                data-testid={`tour-card-${tour.id}`}
                className="group h-full flex flex-col glass-card rounded-2xl border border-gold/15 overflow-hidden hover:-translate-y-2 hover:border-gold/50 hover:shadow-[0_24px_60px_rgba(0,0,0,0.55)] transition-all duration-500"
              >
                <Link to={`/tours/${tour.id}`} data-testid={`tour-link-${tour.id}`} className="relative block h-52 overflow-hidden">
                  <img
                    src={tour.image}
                    alt={tour.title[lang]}
                    loading="lazy"
                    className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-[1.4s] ease-out"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#12131A] via-transparent to-transparent" />
                  <span className="absolute top-4 left-4 rounded-full bg-[#0B0C10]/75 backdrop-blur px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-gold ring-1 ring-gold/40">
                    {tour.tag[lang]}
                  </span>
                </Link>
                <div className="flex flex-1 flex-col p-6">
                  <Link to={`/tours/${tour.id}`}>
                    <h3 className="font-display text-xl sm:text-2xl text-slate-100 leading-snug hover:text-gold-light transition-colors">{tour.title[lang]}</h3>
                  </Link>
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400 font-mono">
                    <span className="inline-flex items-center gap-1.5"><Clock size={12} className="text-gold" />{tour.duration[lang]}</span>
                    <span className="inline-flex items-center gap-1.5"><Route size={12} className="text-gold" />{tour.route[lang]}</span>
                  </div>
                  <p className="mt-3 font-display text-lg text-gold-light" data-testid={`tour-price-${tour.id}`}>
                    {tour.price ? tour.price[lang] : t.tours.priceOnRequest}
                  </p>
                  <ul className="mt-4 space-y-1.5">
                    {tour.highlights[lang].map((h) => (
                      <li key={h} className="text-sm text-slate-400 flex items-start gap-2">
                        <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gold" />
                        {h}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 pt-4 border-t border-gold/10 flex items-center gap-3">
                    <button
                      data-testid={`tour-enquire-${tour.id}`}
                      onClick={() => onEnquire(tour.title.en)}
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-gold to-gold-amber px-4 py-2.5 text-xs sm:text-sm font-bold text-obsidian hover:shadow-[0_0_24px_rgba(212,175,55,0.4)] transition-shadow"
                    >
                      {t.tours.enquire}
                      <ArrowRight size={14} />
                    </button>
                    <a
                      data-testid={`tour-whatsapp-${tour.id}`}
                      href={waLink(i, `Namaste Explore India! I am interested in the "${tour.title.en}" tour (${tour.duration.en}).`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center rounded-full border border-gold/40 px-4 py-2.5 text-xs sm:text-sm font-semibold text-gold-light hover:bg-gold/10 transition-colors"
                    >
                      {t.tours.whatsapp}
                    </a>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Tours;
